import React, { useState } from 'react';
import { supabase } from '../supabaseClient'; 
import { jsPDF } from 'jspdf';
import { FaPlane, FaMagic, FaArrowLeft, FaSpinner } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { callAmadeusProxy, callOpenAIChatCompletion } from '../services/secureProxy';

export default function CreatePassport() {
  const [request, setRequest] = useState('');
  const [loading, setLoading] = useState(false);
  
  const generateDocId = () => `QC-${Math.floor(10000 + Math.random() * 90000)}`;

  // --- 1. LOGIQUE AMADEUS SÉCURISÉE VIA PROXY ---
  const fetchRealFlights = async (destinationIATA: string) => {
    try {
      const data = await callAmadeusProxy('v2/shopping/flight-offers', {
        originLocationCode: 'YUL',
        destinationLocationCode: destinationIATA,
        departureDate: '2026-06-15',
        adults: 1,
        max: 3,
      });

      return data?.data || data?.results || [];
    } catch (err) {
      console.warn('Amadeus proxy indisponible (mode dégradé activé):', err);
      return [];
    }
  };

  // --- 2. SAUVEGARDE SUPABASE ---
  const saveToHistory = async (docId: string, content: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { error } = await supabase.from('passports').insert([
        {
          user_id: user.id,
          doc_id: docId,
          request_text: request,
          full_content: content
        }
      ]);
      if (error) console.error("Erreur sauvegarde:", error);
    }
  };

  // --- 3. GENERATION PDF (DESIGN PREMIUM) ---
  const createAndDownloadPDF = async (text: string, docId: string) => {
    const doc = new jsPDF();
    const { data: { user } } = await supabase.auth.getUser();
    const userEmail = user?.email || "voyageur@forfeo.com";
    
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;

    const themeColor = "#2563eb"; 
    const secondaryColor = "#64748b"; 
    const black = "#000000";

    const addHeader = (pageNum: number) => {
      doc.setFillColor(themeColor);
      doc.rect(0, 0, pageWidth, 15, "F");

      doc.setFont("helvetica", "bold");
      doc.setFontSize(24); doc.setTextColor(themeColor); doc.text("FORFEO Explore.", margin, 35);
      doc.setFontSize(10); doc.setTextColor(secondaryColor); doc.text("VOTRE COMPAGNON DE ROUTE INTELLIGENT", margin, 42);
      
      doc.setFontSize(10); doc.setTextColor(secondaryColor); doc.text("ID DOCUMENT", pageWidth - margin, 35, { align: "right" });
      doc.setFontSize(12); doc.setTextColor(black); doc.text(docId, pageWidth - margin, 42, { align: "right" });
      
      doc.setDrawColor(220); doc.line(margin, 50, pageWidth - margin, 50);

      return 60; 
    };

    const addFooter = (pageNum: number) => {
      doc.setDrawColor(220); doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
      doc.setFontSize(7); doc.setTextColor(secondaryColor);
      const disclaimer = "AVIS : Ce document est généré par IA à titre indicatif. Forfeo Explore décline toute responsabilité. L'utilisateur est seul responsable.";
      doc.text(doc.splitTextToSize(disclaimer, contentWidth), margin, pageHeight - 12);
    };

    let yPos = addHeader(1);

    doc.setFontSize(9); doc.setTextColor(secondaryColor); doc.text("TITULAIRE:", margin, yPos);
    doc.setFontSize(11); doc.setTextColor(black); doc.setFont("helvetica", "bold"); doc.text(userEmail, margin + 25, yPos);
    yPos += 6;
    doc.setFontSize(9); doc.setTextColor(secondaryColor); doc.setFont("helvetica", "normal"); doc.text("STATUT:", margin, yPos);
    doc.setFontSize(10); doc.setTextColor(themeColor); doc.setFont("helvetica", "bold"); doc.text("MEMBRE CLUB FORFEO", margin + 25, yPos);
    yPos += 15;

    doc.setFontSize(16); doc.setTextColor(black); doc.text("VOTRE ITINÉRAIRE SUR MESURE", margin, yPos);
    yPos += 10;

    const lines = text.split('\n');
    
    lines.forEach((line) => {
        if (yPos > pageHeight - 30) {
            doc.addPage();
            yPos = addHeader(doc.internal.getNumberOfPages());
            addFooter(doc.internal.getNumberOfPages());
        }

        const trimmed = line.trim();

        if (trimmed.match(/^(Jour|Day|Étape)\s+\d+/i)) {
            yPos += 6;
            doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(themeColor);
            doc.text(trimmed, margin, yPos);
            yPos += 8;
        } 
        else if (trimmed.startsWith('-')) {
            doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(black);
            const cleanLine = trimmed.substring(1).trim();
            const splitLines = doc.splitTextToSize(cleanLine, contentWidth - 5);
            doc.text("•", margin, yPos);
            doc.text(splitLines, margin + 5, yPos);
            yPos += (splitLines.length * 5) + 2;
        }
        else if (trimmed.length > 0) {
            doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(black);
            const splitLines = doc.splitTextToSize(trimmed, contentWidth);
            doc.text(splitLines, margin, yPos);
            yPos += (splitLines.length * 5) + 2;
        }
    });

    addFooter(1);
    doc.save(`Passeport-Forfeo-${docId}.pdf`);
  };

  const buildFallbackItinerary = (input: string) => {
    return `Jour 1\n- Arrivée et installation. Prenez le temps de découvrir le quartier, repérer les transports et noter vos premières adresses locales.\n\nJour 2\n- Expérience culturelle et découverte gastronomique. Forfeo recommande de combiner une activité signature avec une adresse locale bien notée.\n\nJour 3\n- Journée flexible : nature, détente ou excursion selon votre rythme. Ajoutez une marge pour les découvertes spontanées.\n\nConseil Forfeo\n- Vérifiez toujours les horaires, disponibilités, politiques d’annulation et conditions météo avant de réserver.\n\nRésumé de votre demande\n- ${input}\n\nDESTINATION_IATA: YUL`;
  };

  const generateTextWithForfeoAI = async () => {
    try {
      const aiData = await callOpenAIChatCompletion({
        model: 'gpt-4o-mini',
        messages: [{
          role: 'system', 
          content: "Tu es un expert Forfeo. Génère un itinéraire clair jour par jour. Utilise des tirets (-) pour les listes. À la fin, ajoute 'DESTINATION_IATA: XXX' (Code aéroport)."
        }, {
          role: 'user', content: request 
        }],
        temperature: 0.7,
      });

      const fullText = aiData.choices?.[0]?.message?.content;
      if (!fullText) throw new Error('Réponse IA vide');
      return fullText;
    } catch (error) {
      console.warn('Forfeo AI indisponible, itinéraire local de secours généré:', error);
      return buildFallbackItinerary(request);
    }
  };

  // --- 4. LOGIQUE PRINCIPALE ---
  const generateItinerary = async () => {
    if (!request) return alert("Veuillez décrire votre voyage !");
    setLoading(true);

    try {
      const fullText = await generateTextWithForfeoAI();
      const iataMatch = fullText.match(/DESTINATION_IATA:\s*([A-Z]{3})/);
      const iataCode = iataMatch ? iataMatch[1] : 'YUL';
      
      const realFlights = await fetchRealFlights(iataCode);
      
      let flightInfo = "";
      if (realFlights.length > 0) {
        flightInfo = "\n\nVOLS SUGGÉRÉS (Amadeus):\n";
        realFlights.slice(0, 3).forEach((f: any, i: number) => {
          const carrier = f?.itineraries?.[0]?.segments?.[0]?.carrierCode || 'Transporteur';
          const total = f?.price?.total || 'N/D';
          const currency = f?.price?.currency || 'CAD';
          flightInfo += `- Option ${i+1}: ${carrier} (${total} ${currency})\n`;
        });
      }

      const finalContent = fullText.replace(/DESTINATION_IATA:\s*[A-Z]{3}/, "") + flightInfo;
      const docId = generateDocId();
      
      // 1. Générer le PDF (Nouveau Design)
      await createAndDownloadPDF(finalContent, docId);
      // 2. Sauvegarder dans Supabase
      await saveToHistory(docId, finalContent);

      alert("Passeport généré et sauvegardé dans votre Club ! ✈️");

    } catch (error: any) {
      alert("Erreur : " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      <Header />
      <div className="container mx-auto px-4 py-24 max-w-3xl">
        <a href="/club" className="flex items-center gap-2 text-slate-500 hover:text-blue-600 mb-8 transition font-bold text-sm">
            <FaArrowLeft /> Retour au Club Forfeo
        </a>
        <div className="bg-white p-8 rounded-3xl shadow-xl border border-slate-200">
            <div className="flex items-center gap-4 mb-8">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-2xl flex items-center justify-center text-3xl shadow-lg shadow-blue-200">
                    <FaPlane />
                </div>
                <div>
                    <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tighter">Mon Passeport</h1>
                    <p className="text-slate-500 font-bold text-xs uppercase tracking-widest">Génération d'itinéraire intelligent</p>
                </div>
            </div>
            <div className="mb-8">
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-3 tracking-widest">
                    Détaillez votre projet de voyage
                </label>
                <textarea 
                    className="w-full h-48 p-5 rounded-2xl bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-blue-500 outline-none resize-none font-medium text-slate-700 transition"
                    placeholder="Ex: Je pars 7 jours à Paris en juin. J'aime l'art, la gastronomie et je veux visiter Versailles..."
                    value={request}
                    onChange={(e) => setRequest(e.target.value)}
                ></textarea>
            </div>
            <button 
                onClick={generateItinerary}
                disabled={loading}
                className="w-full py-5 bg-slate-900 text-white font-black uppercase tracking-widest rounded-2xl shadow-xl hover:bg-blue-600 transition transform active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
            >
                {loading ? <FaSpinner className="animate-spin" /> : <><FaMagic /> Générer le Passeport</>}
            </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}
